import Box from "@mui/joy/Box"
import Container, { type ContainerProps } from "@mui/joy/Container"
import { type PropsWithChildren, useEffect } from "react"
import { useStore } from "zustand"
import { useAccount } from "@levana/chain/account"
import { ModalPresenter } from "@levana/utils/modal"
import { Footer } from "rujira.ui"

import BannerProvider from "@perps/banner/BannerContext"
import AnnouncementsBanner from "@future/banner/AnnouncementsBanner"
import { updateTargetDataInReports } from "@future/target/state"
import UpdatesBanner from "@future/banner/UpdatesBanner"
import ReportEnabledDecider from "@future/libs/error/ReportEnabledDecider"
import type { ContextStoreProp } from "@future/context/store"
import { useClosedPositionsStore } from "@future/pages/tradeMarket/openPositions/useClosedPositionsStore"
import ConnectModal from "@future/connect/ConnectModal"
import Header from "@future/libs/theme/Header"
import { useClosedLimitOrdersStore } from "@future/pages/tradeMarket/limitOrders/useClosedLimitOrdersStore"
import { RouteRootId } from "@future/router/types"
import RujiraLink from "@future/rujira/RujiraLink"

import OnlineWatcher from "../OnlineWatcher"

// TODO: merge this with the new RootLayout
const RootLayout_Inner = (
  props: PropsWithChildren<ContextStoreProp<"standard">>,
) => {
  const routeRootId = useStore(
    props.contextStore,
    (state) => state.route.rootId,
  )

  useClearClosedPositions(props)
  useSyncReportMetadata(props)

  return (
    <>
      <Box sx={{ marginTop: 11 }} />
      <BannerProvider>
        <ReportEnabledDecider />
        <OnlineWatcher />
        <AnnouncementsBanner />
        <UpdatesBanner />

        <ModalPresenter queueKey={ConnectModal.name}>
          <ConnectModal contextStore={props.contextStore} />
        </ModalPresenter>

        <Header
          contextStore={props.contextStore}
          levana={{ includeAccount: routeRootId !== "stats" }}
        />

        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            flexWrap: "nowrap",
            justifyContent: "space-between",
          }}
        >
          <Container
            maxWidth={getMaxBreakpoint(routeRootId)}
            sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
          >
            {props.children}
          </Container>
          <Box
            sx={{
              background: "#0C0A0F",
              mt: 3,
            }}
          >
            <Container maxWidth={getMaxBreakpoint(routeRootId)}>
              <Footer routerElement={RujiraLink} />
            </Container>
          </Box>
        </Box>
      </BannerProvider>
    </>
  )
}

const getMaxBreakpoint = (
  rootId: RouteRootId | undefined,
): NonNullable<ContainerProps["maxWidth"]> => {
  switch (rootId) {
    case RouteRootId.markets:
      return "xl"
    case RouteRootId.earn:
      return "xl"
    case RouteRootId.history:
      return "xl"
    case RouteRootId.leaderboard:
      return "xl"
    case RouteRootId.stats:
      return "xl"
    case RouteRootId.trade:
      return false
    case undefined:
      return "xl"
  }
}

const useClearClosedPositions = (props: ContextStoreProp<"standard">) => {
  const targetId = useStore(props.contextStore, (state) => state.targetInfo.id)
  const { data: account } = useAccount()

  // biome-ignore lint/correctness/useExhaustiveDependencies: clear for `targetId`
  useEffect(() => {
    if (!account) {
      useClosedPositionsStore.getState().clear()
      useClosedLimitOrdersStore.getState().clear()
    }
  }, [targetId, account])
}

const useSyncReportMetadata = (props: ContextStoreProp<"standard">) => {
  const targetId = useStore(props.contextStore, (state) => state.targetInfo.id)
  const factoryAddress = useStore(
    props.contextStore,
    (state) => state.chain.config.factoryAddress,
  )

  useEffect(() => {
    updateTargetDataInReports(targetId, factoryAddress)
  }, [targetId, factoryAddress])
}

export default RootLayout_Inner
